/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from "react"
import { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"
import { object, string } from "yup"
import moment from "moment"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { httpGet, httpPost } from "../services/Http.service"
import { apiUrl } from "../configs/config.constants"
import { resolveAssetBillingPeriod } from "../utils/billingPeriod"
import { ProjectContext } from "../App"
import {
  RentalAssetGroup,
  RentalAssetSummary,
  RentalChargePreview,
  RentalRequest,
} from "../types/rental.type"
import {
  MAX_IDENTITY_MB,
  identityImageErrorKey,
  readIdentityImage,
} from "../utils/identityImage"
import {
  CustomerQr,
  extractQrCode,
  mapCustomerQrToRentForm,
} from "../utils/customerQr"

export type RentForm = {
  assetId: string
  startDate: string
  expectedReturnDate: string
  customerName: string
  customerPhonePrefix: string
  customerPhoneNo: string
  customerEmail: string
  customerAddress: string
  notes: string
}

export type IdentityImageState = {
  dataUrl: string
  fileName: string
}

/**
 * One whole billing period after the start, so a monthly item opens on a month rather than
 * on tomorrow. Rentals bill in whole periods, and a range shorter than one costs the same
 * as a full one — showing tomorrow would quietly quote a month for a day.
 *
 * Never less than a day, since the field holds a date and hourly items would otherwise
 * suggest returning before they were collected.
 */
function defaultReturnDate(startDate: string, asset: RentalAssetSummary | null): string {
  const start = moment(startDate, 'YYYY-MM-DD')
  const nextDay = start.clone().add(1, 'day')
  if (!asset) return nextDay.format('YYYY-MM-DD')

  const period = resolveAssetBillingPeriod(asset)
  const end = start.clone().add(period.interval, `${period.unit}s`)
  return moment.max(end, nextDay).format('YYYY-MM-DD')
}

export function useRentForm() {
  const { project } = useContext(ProjectContext)
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('rent')

  const [groups, setGroups] = useState<RentalAssetGroup[]>([])
  const [assets, setAssets] = useState<RentalAssetSummary[]>([])
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(true)
  const [preview, setPreview] = useState<RentalChargePreview | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [identity, setIdentity] = useState<IdentityImageState | null>(null)
  const [identityError, setIdentityError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [hasEditedReturnDate, setHasEditedReturnDate] = useState(false)
  const [qrStatus, setQrStatus] = useState<{
    state: 'idle' | 'loading' | 'error' | 'success'
    message: string
  }>({ state: 'idle', message: '' })

  const cachedCustomerData = JSON.parse(localStorage.getItem('customerData') || '{}')

  const validationSchema = useMemo(
    () =>
      object().shape({
        assetId: string().required(t('validation.chooseItem')),
        customerName: string().required(t('validation.enterFullName')),
        customerPhoneNo: string().required(t('validation.enterPhone')),
        customerPhonePrefix: string().required(t('validation.selectCountryCode')),
        // Empty string is treated as unset so the field stays optional.
        customerEmail: string()
          .transform((value) => (!value || !String(value).trim() ? undefined : String(value).trim()))
          .email(t('validation.enterValidEmail'))
          .optional(),
        customerAddress: string().required(t('validation.enterAddress')),
        startDate: string().required(t('validation.pickStartDate')),
        expectedReturnDate: string()
          .required(t('validation.pickReturnDate'))
          .test('after-start', t('validation.returnAfterStart'), (value, ctx) => {
            if (!value || !ctx.parent.startDate) return true
            return moment(value).isAfter(moment(ctx.parent.startDate), 'day')
          }),
        notes: string().optional().nullable(),
      }),
    [t, i18n.language]
  )

  const formik = useFormik<RentForm>({
    enableReinitialize: true,
    initialValues: {
      assetId: '',
      startDate: moment().format('YYYY-MM-DD'),
      expectedReturnDate: moment().add(1, 'day').format('YYYY-MM-DD'),
      customerName: cachedCustomerData.name || '',
      customerPhonePrefix: cachedCustomerData.phonePrefix || '+62',
      customerPhoneNo: cachedCustomerData.phoneNo || '',
      customerEmail: cachedCustomerData.email || '',
      customerAddress: cachedCustomerData.address || '',
      notes: '',
    },
    validationSchema,
    onSubmit: (values) => submitRequest(values),
  })

  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.assetId === formik.values.assetId) || null,
    [assets, formik.values.assetId]
  )

  const loadCatalog = async () => {
    setIsLoadingCatalog(true)
    try {
      const [groupResponse, assetResponse] = await Promise.all([
        httpGet(apiUrl.objectRental.groups),
        httpGet(apiUrl.objectRental.assets),
      ])
      setGroups(groupResponse.data?.data || [])
      setAssets(assetResponse.data?.data || [])
    } catch (error) {
      console.error(error)
      setGroups([])
      setAssets([])
    } finally {
      setIsLoadingCatalog(false)
    }
  }

  /**
   * Quoted by the server rather than worked out here: the rounding rules (whole billing
   * periods, minimum one) live with the charge that will actually be raised.
   */
  const loadPreview = async (assetId: string, startDate: string, expectedReturnDate: string) => {
    if (!assetId || !startDate || !expectedReturnDate) {
      setPreview(null)
      return
    }
    setIsLoadingPreview(true)
    try {
      const response: AxiosResponse<RentalChargePreview> = await httpGet(
        apiUrl.objectRental.chargePreview,
        { assetId, startDate, expectedReturnDate }
      )
      setPreview(response.data)
    } catch (error) {
      console.error(error)
      setPreview(null)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const pickIdentity = async (file: File) => {
    setIdentityError('')
    try {
      const prepared = await readIdentityImage(file)
      setIdentity({ dataUrl: prepared.dataUrl, fileName: prepared.fileName })
    } catch (error) {
      console.error(error)
      setIdentity(null)
      setIdentityError(t(identityImageErrorKey(error), { max: MAX_IDENTITY_MB }))
    }
  }

  const clearIdentity = () => {
    setIdentity(null)
    setIdentityError('')
  }

  /**
   * Picks an item and moves the return date onto its billing period in the same update, so
   * the estimate is never briefly quoted for a range the customer never saw.
   */
  const selectAsset = (assetId: string) => {
    formik.setFieldValue('assetId', assetId)

    if (hasEditedReturnDate) return
    const asset = assets.find((candidate) => candidate.assetId === assetId) || null
    formik.setFieldValue(
      'expectedReturnDate',
      defaultReturnDate(formik.values.startDate, asset)
    )
  }

  /**
   * Fills the contact fields from a customer QR — the same code the booking form takes, so
   * one saved QR serves both flows.
   *
   * Only who they are is filled. The item, the dates and the ID photo are what they came
   * here to give, and the request still identifies the customer by phone rather than by the
   * resolved id: this page is public, so trusting an id from it would let anyone file a
   * rental against someone else's record.
   */
  const applyCustomerQr = async (code: string) => {
    if (!code) return

    setQrStatus({ state: 'loading', message: '' })
    try {
      const response: AxiosResponse<CustomerQr> = await httpPost(apiUrl.customerQr.resolve, { code })
      const fields = mapCustomerQrToRentForm(response.data)

      Object.entries(fields).forEach(([field, value]) => {
        if (value) formik.setFieldValue(field, value)
      })

      setQrStatus({
        state: 'success',
        message: t('qr.filled', { name: response.data.name || '' }),
      })
    } catch (error) {
      console.error(error)
      setQrStatus({ state: 'error', message: t('qr.invalid') })
    }
  }

  const submitRequest = async (values: RentForm) => {
    if (isSubmitting) return
    if (!identity) {
      setIdentityError(t('validation.addIdentity'))
      return
    }

    setIsSubmitting(true)
    setSubmitError('')
    try {
      const payload = {
        assetId: values.assetId,
        startDate: values.startDate,
        expectedReturnDate: values.expectedReturnDate,
        notes: values.notes || undefined,
        tenant: {
          name: values.customerName,
          phoneNo: values.customerPhonePrefix + values.customerPhoneNo,
          email: values.customerEmail || undefined,
          address: values.customerAddress,
        },
        identityDocument: {
          file: identity.dataUrl,
          file_name: identity.fileName,
        },
      }

      const response: AxiosResponse<RentalRequest> = await httpPost(apiUrl.objectRental.request, payload)

      // Shared with the booking form, so a customer who has booked before does not retype
      // their details (and vice versa).
      localStorage.setItem('customerData', JSON.stringify({
        ...cachedCustomerData,
        name: values.customerName,
        phonePrefix: values.customerPhonePrefix,
        phoneNo: values.customerPhoneNo,
        email: values.customerEmail,
        address: values.customerAddress,
      }))
      localStorage.setItem('rentalRequestData', JSON.stringify(response.data))
      navigate('/rent/confirmation', { state: { request: response.data } })
    } catch (error) {
      console.error(error)
      const status = (error as AxiosError)?.response?.status
      // 409 is the one failure a customer can act on: someone else took the item while
      // they were filling the form, so the catalogue is reloaded behind the message.
      if (status === 409) {
        setSubmitError(t('errors.assetTaken'))
        formik.setFieldValue('assetId', '')
        void loadCatalog()
      } else {
        setSubmitError(t('errors.submitFailed'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (!project) return
    void loadCatalog()
  }, [project])

  // A QR opened straight from a phone camera lands here as ?c=<code>. Waits for the
  // project, since resolving needs the project-id header it puts in localStorage.
  useEffect(() => {
    if (!project) return

    const code = extractQrCode(window.location.search)
    if (code) void applyCustomerQr(code)
  }, [project])

  useEffect(() => {
    void loadPreview(
      formik.values.assetId,
      formik.values.startDate,
      formik.values.expectedReturnDate
    )
  }, [formik.values.assetId, formik.values.startDate, formik.values.expectedReturnDate])

  /**
   * The return date tracks the item's billing period until the customer sets one of their
   * own, after which it is theirs and only gets corrected when it falls on or before the
   * start (which would be no rental at all).
   */
  useEffect(() => {
    const { startDate, expectedReturnDate } = formik.values
    if (!startDate) return

    if (!hasEditedReturnDate) {
      const suggested = defaultReturnDate(startDate, selectedAsset)
      if (suggested !== expectedReturnDate) formik.setFieldValue('expectedReturnDate', suggested)
      return
    }

    if (!expectedReturnDate) return
    if (moment(expectedReturnDate).isAfter(moment(startDate), 'day')) return
    formik.setFieldValue('expectedReturnDate', defaultReturnDate(startDate, selectedAsset))
  }, [formik.values.startDate, selectedAsset, hasEditedReturnDate])

  return {
    formik,
    groups,
    assets,
    isLoadingCatalog,
    selectedAsset,
    preview,
    isLoadingPreview,
    identity,
    identityError,
    pickIdentity,
    clearIdentity,
    isSubmitting,
    submitError,
    reloadCatalog: loadCatalog,
    selectAsset,
    markReturnDateEdited: () => setHasEditedReturnDate(true),
    applyCustomerQr,
    qrStatus,
    setQrStatus,
  }
}
